import { createContext, useContext } from 'react';

/**
 * El objeto de contexto de facturación y sus lectores, SEPARADOS del provider.
 *
 * POR QUÉ VIVE EN SU PROPIO FICHERO
 * ---------------------------------
 * `BillingContext.jsx` importa `apiClient` y `AuthContext`, y `AuthContext`
 * arrastra `supabaseClient`, `postgresBackendService` e **`i18n`**. Importar el
 * provider sólo para leer el contexto metía toda esa cadena en cualquier
 * componente que quisiera saber si el plan está agotado.
 *
 * No es teórico: al añadir `useCreditGate` a `AddPresaPanel`, esa cadena
 * inicializó i18next con su Suspense y el panel **dejó de pintar nada** — 34
 * tests en rojo por un import, sin ningún error visible, sólo un `<div />`
 * vacío. Separar el contexto corta la cadena de raíz.
 *
 * Quien necesite el estado importa de AQUÍ. `BillingContext.jsx` sigue
 * exportando lo mismo por compatibilidad.
 */
export const BillingContext = createContext(null);

/**
 * Lector estricto: revienta fuera del provider, que es lo que quieres en un
 * componente que depende de facturación — el throw dice que está mal montado.
 */
export const useBilling = () => {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error('useBilling must be used within BillingProvider');
  return ctx;
};

/**
 * Lector tolerante: devuelve `null` fuera del provider.
 *
 * Para componentes de CARCASA o de hoja que se montan en muchos sitios —
 * `UserMenu` sale en ocho pantallas, algunas anteriores al alta. Ahí «no hay
 * datos de consumo» es un estado legítimo: no se pinta el aviso y ya está.
 *
 * NO lo uses para esquivar el error en un componente que sí depende de billing.
 */
export const useBillingOptional = () => useContext(BillingContext);
